import { apiClient, externalApiClient } from '../../services/api';
import md5 from 'md5';

const profileApi = {
  getProfile: () => apiClient.get('/profile').then(r => r.data),

  updateBasic: (data) => apiClient.put('/profile/basic', data).then(r => r.data),

  updateContact: (data) => apiClient.put('/profile/contact', data).then(r => r.data),

  updatePhoto: (url) => apiClient.put('/profile/photo', { profile_picture_url: url }).then(r => r.data),

  updateAnthropicKey: (api_key) =>
    apiClient.put('/profile/anthropic-key', { api_key }).then(r => r.data),
  deleteAnthropicKey: () => apiClient.delete('/profile/anthropic-key').then(r => r.data),
  deleteGeminiKey: () => apiClient.delete('/profile/gemini-key').then(r => r.data),
  updatePlannerSettings: (data) =>
    apiClient.put('/profile/planner-settings', data).then(r => r.data),
  updateFaceGroupingSettings: (data) =>
    apiClient.put('/profile/face-grouping-settings', data).then(r => r.data),

  updateCaptionSettings: (data) =>
    apiClient.put('/profile/caption-settings', data).then(r => r.data),
  deleteCaptionGeminiKey: () =>
    apiClient.delete('/profile/caption-gemini-key').then(r => r.data),

  checkUsername: (username) =>
    apiClient.get('/profile/username/check', { params: { username } }).then(r => r.data),

  setUsername: (username) => apiClient.put('/profile/username', { username }).then(r => r.data),

  setVisibility: (is_public) => apiClient.put('/profile/visibility', { is_public }).then(r => r.data),

  getPublicProfile: (username) => apiClient.get(`/public/profile/${username}`).then(r => r.data),

  // "Add to Profile View" showcase grid
  getMyShowcasePhotos: () => apiClient.get('/profile/showcase').then(r => r.data),
  addShowcasePhoto: (event_id, photo_id) =>
    apiClient.post('/profile/showcase', { event_id, photo_id }).then(r => r.data),
  removeShowcasePhoto: (event_id, photo_id) =>
    apiClient.delete('/profile/showcase', { data: { event_id, photo_id } }).then(r => r.data),

  changePassword: (otp, new_password) =>
    apiClient.post('/profile/change_password', { otp, new_password }).then(r => r.data),

  sendOtp: (phone_number, country_code = '+44') =>
    externalApiClient.post('/send_otp', { phone_number, country_code }).then(r => r.data),
  // Email change (verified)
  sendEmailChangeOtp: (new_email) =>
    externalApiClient.post('/profile/email_change/send_otp', { new_email }).then(r => r.data),
  confirmEmailChange: (new_email, otp) =>
    apiClient.put('/profile/email', { new_email, otp }).then(r => r.data),

  // Phone add/change (verified)
  sendPhoneChangeOtp: (new_phone, country_code = '+44') =>
    externalApiClient.post('/profile/phone_change/send_otp', { new_phone, country_code }).then(r => r.data),
  confirmPhoneChange: (new_phone, otp, country_code = '+44') =>
    apiClient.put('/profile/phone', { new_phone, country_code, otp }).then(r => r.data),
  // Firebase Phone Auth path — sends the idToken returned by verifyPhoneOtp()
  confirmPhoneChangeFirebase: (firebase_id_token) =>
    apiClient.put('/profile/phone', { firebase_id_token }).then(r => r.data),

  // Addresses
  listAddresses: () => apiClient.get('/address').then(r => r.data),
  addAddress: (address) => apiClient.post('/address', { address }).then(r => r.data),
  updateAddress: (id, address) => apiClient.put(`/address/${id}`, { address }).then(r => r.data),
  deleteAddress: (id) => apiClient.delete(`/address/${id}`).then(r => r.data),
  setDefaultAddress: (id) => apiClient.patch(`/address/${id}/default`).then(r => r.data),

  // Photo presigned URL
  getPhotoPresignedUrl: (fileName, contentType, customerId) =>
    apiClient
      .get('/generate_presigned_url', {
        params: { file_name: fileName, content_type: contentType, prefix: `profiles/${customerId}` },
      })
      .then(r => r.data),

  // WebAuthn / Passkeys
  webAuthnRegisterStart: () =>
    apiClient.post('/2fa/register/start', { client_origin: window.location.origin }, { timeout: 10000 }).then(r => r.data),
  webAuthnRegisterComplete: (credential, friendly_name) =>
    apiClient.post('/2fa/register/complete', { ...credential, friendly_name, client_origin: window.location.origin }, { timeout: 10000 }).then(r => r.data),
  webAuthnAuthStart: () =>
    apiClient.post('/2fa/authenticate/start', { client_origin: window.location.origin }, { timeout: 10000 }).then(r => r.data),
  webAuthnAuthComplete: (assertion) =>
    apiClient.post('/2fa/authenticate/complete', { ...assertion, client_origin: window.location.origin }, { timeout: 10000 }).then(r => r.data),
  listCredentials: () => apiClient.get('/2fa/credentials').then(r => r.data),
  deleteCredential: (id) => apiClient.delete(`/2fa/credentials/${id}`).then(r => r.data),

  // Change password (hashes client-side before sending)
  changePasswordHashed: (otp, newPassword) =>
    apiClient.post('/profile/change_password', { otp, new_password: md5(newPassword) }).then(r => r.data),
  // Firebase Phone Auth path — sends idToken instead of OTP, md5-hashes password
  changePasswordFirebase: (firebase_id_token, newPassword) =>
    apiClient.post('/profile/change_password', { firebase_id_token, new_password: md5(newPassword) }).then(r => r.data),

  // ── Billing & Credits ──────────────────────────────────────────────────────
  getBillingBalance: () =>
    apiClient.get('/billing/balance').then(r => r.data),

  calculateCredits: (amount_usd) =>
    apiClient.post('/billing/calculate', { amount_usd }).then(r => r.data),

  createOrder: (amount_usd) =>
    apiClient.post('/billing/create-order', { amount_usd }).then(r => r.data),

  redeemPurchaseCode: (amount_usd, purchase_code) =>
    apiClient.post('/billing/redeem-purchase-code', { amount_usd, purchase_code }).then(r => r.data),

  simulatePayment: (gateway_order_id) =>
    apiClient.post('/billing/simulate-payment', { gateway_order_id }).then(r => r.data),

  submitPaymentWebhook: (razorpay_payment_id, razorpay_order_id, razorpay_signature) =>
    apiClient.post('/billing/webhook', {
      razorpay_payment_id,
      razorpay_order_id,
      razorpay_signature,
    }).then(r => r.data),

  getBillingTransactions: (limit = 20, offset = 0) =>
    apiClient.get('/billing/transactions', { params: { limit, offset } }).then(r => r.data),

  getBillingPurchases: (limit = 20, offset = 0) =>
    apiClient.get('/billing/purchases', { params: { limit, offset } }).then(r => r.data),

  // Privacy / GDPR controls
  getPrivacyConsents: () => apiClient.get('/privacy/consents').then(r => r.data),
  grantPrivacyConsents: (purposes, eventId = null, source = 'profile') =>
    apiClient.post('/privacy/consents', { purposes, event_id: eventId, source }).then(r => r.data),
  withdrawPrivacyConsent: (purpose, eventId = null) =>
    apiClient.delete(`/privacy/consents/${encodeURIComponent(purpose)}`, {
      params: eventId ? { event_id: eventId } : undefined,
    }).then(r => r.data),
  exportPrivacyData: () => apiClient.get('/privacy/export').then(r => r.data),
  deleteAccount: () => apiClient.delete('/privacy/delete-account', { data: { confirm: 'DELETE' } }).then(r => r.data),

  // Storage profiler
  getStorage: (force = false) =>
    apiClient.get('/profile/storage', { params: force ? { force: 'true' } : {}, timeout: 120000 }).then(r => r.data),

  // Compute usage profiler
  getComputeUsage: (period) =>
    apiClient.get('/profile/compute-usage', { params: period ? { period } : {}, timeout: 90000 }).then(r => r.data),

  // Google Drive connection
  getDriveStatus: () => externalApiClient.get('/google/drive/status').then(r => r.data),
  connectDrive: (authCode, redirectUri = 'postmessage') =>
    externalApiClient.post('/google/drive/connect', { auth_code: authCode, redirect_uri: redirectUri }).then(r => r.data),
  disconnectDrive: () => externalApiClient.delete('/google/drive/disconnect').then(r => r.data),
  linkGoogleAccount: (accessToken) =>
    externalApiClient.post('/googleauth/link', { access_token: accessToken }).then(r => r.data),
  unlinkGoogleAccount: () =>
    externalApiClient.delete('/googleauth/unlink').then(r => r.data),

  // Integration settings (email / WhatsApp share)
  updateIntegrations: (data) =>
    apiClient.put('/profile/integrations', data).then(r => r.data),
};

export default profileApi;
